import React, { useState, useEffect } from 'react';
import { Save, Info, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import apiClient from '../../../api/client';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const WholesaleTaxSettingsPage = () => {
  const [settings, setSettings] = useState({
    vat_enabled: true,
    vat_rate: 7.5,
    vat_pricing_mode: 'exclusive',
    apply_mode: 'new_only'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/settings/tax/wholesale');
        if (response.data?.success && response.data?.data) {
          setSettings({
            vat_enabled: response.data.data.vat_enabled ?? true,
            vat_rate: parseFloat(response.data.data.vat_rate) || 7.5,
            vat_pricing_mode: response.data.data.vat_pricing_mode || 'exclusive',
            apply_mode: response.data.data.apply_mode || 'new_only'
          });
        }
      } catch (error) {
        console.error('Error fetching wholesale tax settings:', error);
        toast.error('Failed to load tax settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();

    // Confirmation for retroactive mode
    if (settings.apply_mode === 'retroactive') {
      const confirmed = window.confirm(
        'Are you sure you want to recalculate tax for all unpaid orders?\n\n' +
          'This will update the total amounts on pending invoices and cannot be automatically undone.'
      );
      if (!confirmed) return;
    }

    try {
      setSaving(true);
      const response = await apiClient.put('/settings/tax/wholesale', settings);
      if (response.data?.success) {
        toast.success('Tax settings saved successfully');
        // Reset apply_mode to new_only after saving
        setSettings((prev) => ({ ...prev, apply_mode: 'new_only' }));
      } else {
        toast.error(response.data?.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving wholesale tax settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save tax settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVatToggle = () => {
    setSettings((prev) => ({ ...prev, vat_enabled: !prev.vat_enabled }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tax Configuration</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Manage VAT settings for wholesale orders
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid gap-6">
        {/* VAT Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">VAT Settings</h2>
            <div className="flex items-center gap-3">
              <span
                className={`text-sm font-medium ${settings.vat_enabled ? 'text-green-600' : 'text-gray-500'}`}
              >
                {settings.vat_enabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                type="button"
                onClick={handleVatToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.vat_enabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.vat_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${!settings.vat_enabled ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                VAT Rate (%)
              </label>
              <input
                type="number"
                value={settings.vat_rate}
                onChange={(e) =>
                  setSettings({ ...settings, vat_rate: parseFloat(e.target.value) || 0 })
                }
                className="w-full border border-gray-300 dark:border-gray-600 p-2 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                step="0.1"
                min="0"
                max="100"
                disabled={!settings.vat_enabled}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                VAT Pricing Mode
              </label>
              <select
                value={settings.vat_pricing_mode}
                onChange={(e) => setSettings({ ...settings, vat_pricing_mode: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 p-2 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={!settings.vat_enabled}
              >
                <option value="exclusive">VAT Exclusive (Added on top)</option>
                <option value="inclusive">VAT Inclusive (Already in price)</option>
              </select>
            </div>
          </div>

          {/* Info Box */}
          <div
            className={`mt-4 p-3 rounded-lg border ${settings.vat_enabled ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'}`}
          >
            <div className="flex items-start gap-2">
              <Info
                className={`w-5 h-5 mt-0.5 flex-shrink-0 ${settings.vat_enabled ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}
              />
              <div className="text-sm">
                {settings.vat_enabled ? (
                  settings.vat_pricing_mode === 'exclusive' ? (
                    <p className="text-blue-800 dark:text-blue-200">
                      <strong>VAT Exclusive:</strong> VAT will be calculated and added on top of
                      product prices in invoices. Example: ₦100,000 order + ₦7,500 VAT = ₦107,500
                      total.
                    </p>
                  ) : (
                    <p className="text-blue-800 dark:text-blue-200">
                      <strong>VAT Inclusive:</strong> VAT is already included in product prices. The
                      VAT amount will be extracted and shown separately on invoices. Example:
                      ₦100,000 order (includes ₦6,977 VAT).
                    </p>
                  )
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">
                    VAT is currently disabled. No tax will be applied to wholesale orders.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Retroactive Application Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Apply to Existing Orders
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose how tax setting changes should affect existing orders.
          </p>

          <div className="space-y-4">
            {/* Option 1: New orders only (default) */}
            <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <input
                type="radio"
                name="apply_mode"
                value="new_only"
                checked={settings.apply_mode !== 'retroactive'}
                onChange={() => setSettings((prev) => ({ ...prev, apply_mode: 'new_only' }))}
                className="mt-1 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
              />
              <div>
                <span className="font-medium text-gray-900 dark:text-white">New orders only</span>
                <span className="ml-2 text-xs px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded-full">
                  Recommended
                </span>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Tax changes will only apply to orders created after saving. Existing invoices and
                  receipts remain unchanged.
                </p>
              </div>
            </label>

            {/* Option 2: Include unpaid orders */}
            <label className="flex items-start gap-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <input
                type="radio"
                name="apply_mode"
                value="retroactive"
                checked={settings.apply_mode === 'retroactive'}
                onChange={() => setSettings((prev) => ({ ...prev, apply_mode: 'retroactive' }))}
                className="mt-1 w-4 h-4 text-emerald-600 focus:ring-emerald-500"
              />
              <div>
                <span className="font-medium text-gray-900 dark:text-white">
                  Apply to existing unpaid orders
                </span>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Recalculate tax for all unpaid/pending orders using the new settings. Already paid
                  or completed orders remain unchanged.
                </p>
              </div>
            </label>
          </div>

          {/* Warning for retroactive mode */}
          {settings.apply_mode === 'retroactive' && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800 dark:text-red-200">
                  <strong>Warning:</strong> This will recalculate amounts for all unpaid orders.
                  Customers will see updated totals on their pending invoices. This action cannot be
                  automatically undone.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-200">Note</h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                These tax settings apply only to the wholesale module. Changes here will not affect
                retail sales tax configuration.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesaleTaxSettingsPage;
